import React, { useState, useEffect } from 'react';
import {
  Megaphone,
  TrendingUp,
  Globe,
  Users,
  Target,
  BarChart2,
  PieChart,
  Plus,
  ArrowUpRight,
  ExternalLink,
  Layers,
  Sparkles,
  Download,
  Share2,
  CheckCircle,
  Eye,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { dbHelpers } from '../../config/database';

interface Campaign {
  id: string;
  name: string;
  channel: 'Google Ads' | 'LinkedIn Ads' | 'SEO Inbound' | 'Webinar / Event' | 'Content Marketing';
  budget: number;
  spent: number;
  impressions: number;
  clicks: number;
  leads: number;
  status: 'Active' | 'Scheduled' | 'Completed' | 'Paused';
}

export const MarketingDashboard: React.FC = () => {
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'campaigns' | 'channels' | 'demand' | 'collateral'>('campaigns');
  const [showCampaignModal, setShowCampaignModal] = useState(false);

  const [campaigns, setCampaigns] = useState<Campaign[]>([
    {
      id: 'CMP-01',
      name: 'Google Search: Enterprise Cloud Solutions',
      channel: 'Google Ads',
      budget: 50000,
      spent: 34200,
      impressions: 48500,
      clicks: 1940,
      leads: 28,
      status: 'Active'
    },
    {
      id: 'CMP-02',
      name: 'LinkedIn B2B: CTO & Engineering Leaders',
      channel: 'LinkedIn Ads',
      budget: 65000,
      spent: 51800,
      impressions: 62000,
      clicks: 2150,
      leads: 34,
      status: 'Active'
    },
    {
      id: 'CMP-03',
      name: 'Organic SEO Engine: React & Node Software Dev',
      channel: 'SEO Inbound',
      budget: 25000,
      spent: 25000,
      impressions: 112000,
      clicks: 6840,
      leads: 52,
      status: 'Active'
    },
    {
      id: 'CMP-04',
      name: 'Tech Webinar: Building Scalable Cloud Apps',
      channel: 'Webinar / Event',
      budget: 15000,
      spent: 14200,
      impressions: 18400,
      clicks: 980,
      leads: 19,
      status: 'Completed'
    }
  ]);

  const [newCampaign, setNewCampaign] = useState({
    name: '',
    channel: 'Google Ads' as Campaign['channel'],
    budget: 30000,
    status: 'Active' as Campaign['status']
  });

  useEffect(() => {
    loadInquiries();
  }, []);

  const loadInquiries = async () => {
    setLoading(true);
    try {
      const res = await dbHelpers.getInquiries();
      if (res?.status && Array.isArray(res.data)) {
        setInquiries(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCampaign.name) return;

    const created: Campaign = {
      id: `CMP-0${campaigns.length + 1}`,
      name: newCampaign.name,
      channel: newCampaign.channel,
      budget: Number(newCampaign.budget) || 20000,
      spent: 0,
      impressions: 0,
      clicks: 0,
      leads: 0,
      status: newCampaign.status
    };

    setCampaigns([created, ...campaigns]);
    setShowCampaignModal(false);
    setNewCampaign({
      name: '',
      channel: 'Google Ads',
      budget: 30000,
      status: 'Active'
    });
  };

  // Aggregated Metrics
  const totalCampaignBudget = campaigns.reduce((acc, c) => acc + c.budget, 0);
  const totalCampaignSpent = campaigns.reduce((acc, c) => acc + c.spent, 0);
  const totalCampaignLeads = campaigns.reduce((acc, c) => acc + c.leads, 0);
  const totalImpressions = campaigns.reduce((acc, c) => acc + c.impressions, 0);
  const totalClicks = campaigns.reduce((acc, c) => acc + c.clicks, 0);
  const avgCTR = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '3.8';
  const avgCPL = totalCampaignLeads > 0 ? Math.round(totalCampaignSpent / totalCampaignLeads) : 950;

  return (
    <div className="flex-1 bg-slate-50 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="bg-purple-100 text-purple-800 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1.5">
                <Megaphone className="w-3.5 h-3.5" />
                Growth & Digital Marketing Hub
              </span>
              <span className="text-xs text-slate-500">Live Traffic & Inbound Engine</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
              Marketing Team Dashboard
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              Welcome, <span className="font-semibold text-slate-800">{user?.name || 'Marketing Specialist'}</span>. Monitor brand outreach, acquisition channels, inbound inquiries, and ROI.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadInquiries}
              disabled={loading}
              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-all flex items-center gap-2 border border-slate-300 shadow-sm cursor-pointer"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh Traffic
            </button>
            <button
              onClick={() => setShowCampaignModal(true)}
              className="px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-xl transition-all shadow-md hover:shadow-lg flex items-center gap-2 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              New Campaign
            </button>
          </div>
        </div>

        {/* Marketing KPI Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Total Impressions</span>
              <Globe className="w-5 h-5 text-indigo-500" />
            </div>
            <div className="text-2xl font-bold text-slate-900">{(totalImpressions / 1000).toFixed(1)}k</div>
            <p className="text-xs text-indigo-600 font-medium mt-1">Cross-channel reach</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Click-Through Rate</span>
              <ArrowUpRight className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold text-emerald-600">{avgCTR}%</div>
            <p className="text-xs text-emerald-700 font-medium mt-1">Above tech industry avg (2.4%)</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Inbound Inquiries</span>
              <Users className="w-5 h-5 text-purple-500" />
            </div>
            <div className="text-2xl font-bold text-purple-600">{inquiries.length + totalCampaignLeads}</div>
            <p className="text-xs text-purple-700 font-medium mt-1">Qualified lead conversions</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-slate-500 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Cost Per Lead (CPL)</span>
              <Target className="w-5 h-5 text-amber-500" />
            </div>
            <div className="text-2xl font-bold text-slate-900">₹{avgCPL.toLocaleString('en-IN')}</div>
            <p className="text-xs text-amber-600 font-medium mt-1">-14% acquisition cost MoM</p>
          </div>

          <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between text-purple-100 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider">Active Campaigns</span>
              <Megaphone className="w-5 h-5 text-purple-200" />
            </div>
            <div className="text-2xl font-bold">{campaigns.filter(c => c.status === 'Active').length} Active</div>
            <p className="text-xs text-purple-100 font-medium mt-1">₹{(totalCampaignSpent / 1000).toFixed(0)}k spent of ₹{(totalCampaignBudget / 1000).toFixed(0)}k</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200 px-6 pt-4 flex space-x-6">
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer ${
                activeTab === 'campaigns'
                  ? 'border-purple-600 text-purple-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <Megaphone className="w-4 h-4" />
              Campaign Performance
              <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full font-bold">
                {campaigns.length}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('channels')}
              className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer ${
                activeTab === 'channels'
                  ? 'border-purple-600 text-purple-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <PieChart className="w-4 h-4" />
              Acquisition Channels
            </button>

            <button
              onClick={() => setActiveTab('demand')}
              className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer ${
                activeTab === 'demand'
                  ? 'border-purple-600 text-purple-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <BarChart2 className="w-4 h-4" />
              Service Demand Analytics
            </button>

            <button
              onClick={() => setActiveTab('collateral')}
              className={`pb-4 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 cursor-pointer ${
                activeTab === 'collateral'
                  ? 'border-purple-600 text-purple-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <Layers className="w-4 h-4" />
              Brand Collateral & Assets
            </button>
          </div>

          {/* Sub-Tab 1: Active Campaigns Tracker */}
          {activeTab === 'campaigns' && (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-slate-700 font-bold uppercase text-xs border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4">Campaign Name</th>
                      <th className="py-3 px-4">Channel</th>
                      <th className="py-3 px-4">Budget / Spent</th>
                      <th className="py-3 px-4">Impressions</th>
                      <th className="py-3 px-4">Clicks (CTR)</th>
                      <th className="py-3 px-4">Leads</th>
                      <th className="py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {campaigns.map(c => {
                      const ctr = c.impressions > 0 ? ((c.clicks / c.impressions) * 100).toFixed(1) : '0';
                      return (
                        <tr key={c.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-4 px-4 font-bold text-slate-900">
                            <div>{c.name}</div>
                            <span className="text-[10px] text-slate-400 font-mono">{c.id}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                              {c.channel}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-xs font-semibold">
                            <div className="text-slate-900">₹{c.spent.toLocaleString('en-IN')}</div>
                            <div className="text-slate-400 font-normal">of ₹{c.budget.toLocaleString('en-IN')}</div>
                          </td>
                          <td className="py-4 px-4 text-slate-800 font-medium">
                            {c.impressions.toLocaleString('en-IN')}
                          </td>
                          <td className="py-4 px-4 text-xs">
                            <span className="font-bold text-slate-900">{c.clicks.toLocaleString('en-IN')}</span>
                            <span className="text-slate-400 ml-1">({ctr}%)</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="font-extrabold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-200">
                              {c.leads} Leads
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span
                              className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                                c.status === 'Active'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : c.status === 'Completed'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {c.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sub-Tab 2: Acquisition Channels */}
          {activeTab === 'channels' && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-4">
                  <h3 className="font-bold text-slate-900 text-base">Traffic & Inbound Channels Distribution</h3>
                  <p className="text-xs text-slate-500">
                    Proportion of prospective clients arriving from specific channels over the past 30 days.
                  </p>

                  <div className="space-y-4 pt-2">
                    {[
                      { name: 'Organic Google Search (SEO)', pct: 38, count: '4,820 visits', color: 'bg-emerald-500' },
                      { name: 'LinkedIn B2B & Social Outreach', pct: 29, count: '3,680 visits', color: 'bg-blue-500' },
                      { name: 'Direct Traffic & Referrals', pct: 21, count: '2,660 visits', color: 'bg-purple-500' },
                      { name: 'Paid Google Search Ads', pct: 12, count: '1,520 visits', color: 'bg-amber-500' }
                    ].map(ch => (
                      <div key={ch.name} className="space-y-1">
                        <div className="flex justify-between text-xs font-semibold text-slate-700">
                          <span>{ch.name}</span>
                          <span>{ch.pct}% ({ch.count})</span>
                        </div>
                        <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${ch.color}`} style={{ width: `${ch.pct}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-4">
                  <h3 className="font-bold text-slate-900 text-base">Conversion Funnel & Engagement</h3>
                  <p className="text-xs text-slate-500">Visitor progression from landing page to consultation inquiry.</p>

                  <div className="space-y-3 pt-2">
                    <div className="bg-white p-3 rounded-xl border border-slate-200 flex justify-between items-center">
                      <div>
                        <div className="text-xs font-bold text-slate-800">1. Total Page Visitors</div>
                        <div className="text-[11px] text-slate-400">Unique corporate visitors</div>
                      </div>
                      <div className="text-lg font-bold text-slate-900">12,680</div>
                    </div>

                    <div className="bg-white p-3 rounded-xl border border-slate-200 flex justify-between items-center">
                      <div>
                        <div className="text-xs font-bold text-slate-800">2. Service & Portfolio Explorers</div>
                        <div className="text-[11px] text-slate-400">Viewed solutions or case studies</div>
                      </div>
                      <div className="text-lg font-bold text-slate-900">4,190 (33%)</div>
                    </div>

                    <div className="bg-white p-3 rounded-xl border border-slate-200 flex justify-between items-center">
                      <div>
                        <div className="text-xs font-bold text-slate-800">3. Contact Form Started</div>
                        <div className="text-[11px] text-slate-400">Engaged with contact inquiry</div>
                      </div>
                      <div className="text-lg font-bold text-slate-900">340 (8.1%)</div>
                    </div>

                    <div className="bg-white p-3 rounded-xl border border-emerald-300 bg-emerald-50/40 flex justify-between items-center">
                      <div>
                        <div className="text-xs font-bold text-emerald-900">4. Consultation Inquiries Submitted</div>
                        <div className="text-[11px] text-emerald-700">Delivered directly to Sales team</div>
                      </div>
                      <div className="text-lg font-bold text-emerald-700">{inquiries.length || 24} Leads</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sub-Tab 3: Service Demand Heatmap */}
          {activeTab === 'demand' && (
            <div className="p-6 space-y-6">
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-slate-900 text-base">Service Demand & Market Interest</h3>
                    <p className="text-xs text-slate-500">
                      Breakdown of solutions requested by clients visiting the website and contacting X2 InfoTech.
                    </p>
                  </div>
                  <span className="bg-purple-100 text-purple-800 text-xs font-bold px-3 py-1 rounded-full">
                    Real-Time Intent
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
                  <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-2">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">36% of Inquiries</span>
                    <h4 className="font-bold text-slate-900 text-sm">Custom Web & Cloud Software</h4>
                    <p className="text-xs text-slate-500">Full-stack React, Next.js, Node.js & Microservices architectures.</p>
                  </div>

                  <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-2">
                    <span className="text-xs font-bold text-purple-600 uppercase tracking-wider">28% of Inquiries</span>
                    <h4 className="font-bold text-slate-900 text-sm">Mobile App Engineering</h4>
                    <p className="text-xs text-slate-500">Cross-platform iOS & Android apps with seamless backend APIs.</p>
                  </div>

                  <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-2">
                    <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">22% of Inquiries</span>
                    <h4 className="font-bold text-slate-900 text-sm">AI Integration & Automation</h4>
                    <p className="text-xs text-slate-500">LLM agents, intelligent workflow automation, and custom ML pipelines.</p>
                  </div>

                  <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-2">
                    <span className="text-xs font-bold text-amber-600 uppercase tracking-wider">14% of Inquiries</span>
                    <h4 className="font-bold text-slate-900 text-sm">Cloud DevOps & Migration</h4>
                    <p className="text-xs text-slate-500">AWS / Azure infrastructure setup, CI/CD pipelines, and security audits.</p>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="bg-purple-50/70 p-4 rounded-xl border border-purple-200 flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-purple-900 space-y-1">
                    <span className="font-bold">Strategic Recommendation for Marketing:</span>
                    <p>
                      High intent detected in Custom Web Apps and AI Automation. Allocating an extra 15% budget toward targeted LinkedIn search campaigns for CTOs and VP Engineering will yield high-ticket enterprise contracts.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sub-Tab 4: Brand Collateral */}
          {activeTab === 'collateral' && (
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
                  <div className="h-28 bg-slate-100 rounded-xl flex items-center justify-center p-4">
                    <img src="/logofull.webp" alt="X2 InfoTech Logo" className="h-10 w-auto object-contain" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Official Full Logo Pack</h4>
                    <p className="text-xs text-slate-500">WebP, PNG, SVG with transparent and white backdrops.</p>
                  </div>
                  <a
                    href="/logofull.webp"
                    download="X2InfoTech_Logo.webp"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-purple-700 hover:text-purple-900"
                  >
                    <Download className="w-3.5 h-3.5" /> Download WebP Logo
                  </a>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
                  <div className="h-28 bg-emerald-50 rounded-xl flex items-center justify-center p-4">
                    <Target className="w-12 h-12 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Company Capability Deck (2026-27)</h4>
                    <p className="text-xs text-slate-500">Executive overview of services, tech stacks, and team credentials.</p>
                  </div>
                  <a
                    href="#portfolio"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-purple-700 hover:text-purple-900"
                  >
                    <Eye className="w-3.5 h-3.5" /> View Portfolio Case Studies
                  </a>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
                  <div className="h-28 bg-blue-50 rounded-xl flex items-center justify-center p-4">
                    <Globe className="w-12 h-12 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Official Letterhead & Brand Palette</h4>
                    <p className="text-xs text-slate-500">Primary Green (#15803d), Emerald (#16a34a), Slate-900, White.</p>
                  </div>
                  <a
                    href="#billing"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-purple-700 hover:text-purple-900"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> View Billing Letterhead Format
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-200">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-lg font-bold text-slate-900">Launch New Marketing Campaign</h3>
              <button onClick={() => setShowCampaignModal(false)} className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer">✕</button>
            </div>

            <form onSubmit={handleCreateCampaign} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">Campaign Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Q3 Cloud Transformation Push"
                  value={newCampaign.name}
                  onChange={e => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">Marketing Channel</label>
                <select
                  value={newCampaign.channel}
                  onChange={e => setNewCampaign({ ...newCampaign, channel: e.target.value as any })}
                  className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white cursor-pointer"
                >
                  <option value="Google Ads">Google Ads (Search & Display)</option>
                  <option value="LinkedIn Ads">LinkedIn B2B Sponsored Outreach</option>
                  <option value="SEO Inbound">SEO & Inbound Tech Content</option>
                  <option value="Webinar / Event">Virtual Webinar / Industry Event</option>
                  <option value="Content Marketing">Case Studies & Whitepapers</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase mb-1">Target Budget (₹)</label>
                <input
                  type="number"
                  min="5000"
                  step="5000"
                  value={newCampaign.budget}
                  onChange={e => setNewCampaign({ ...newCampaign, budget: Number(e.target.value) || 20000 })}
                  className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="pt-3 border-t border-slate-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCampaignModal(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-colors shadow-sm cursor-pointer"
                >
                  Create Campaign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default MarketingDashboard;
